import Map from '@arcgis/core/Map';
import esriConfig from '@arcgis/core/config';
import MapView from '@arcgis/core/views/MapView';
import Attribution from '@arcgis/core/widgets/Attribution';
import { Shape } from '@rsos/constants/mapTypes';
import { normalizeExtent } from '../../utils/normalizeExtent';
import { esriBasemapFromUiStringWithFallback } from './esriBasemapFromUiString';
import './themes/lightTheme.scss';

interface CreateMapParams {
  /** The reference to the DOM element to */
  mapContainer: HTMLElement & { align: string };
  /** Options to create the Map instance with */
  esriMapOptions: __esri.MapProperties & {
    hasAlternativeBasemap?: boolean;
    alternativeBasemapItemIds?: {
      dark?: string;
      light?: string;
    };
  };
  /** Options to create the MapView instance with */
  esriViewOptions: __esri.MapViewProperties & { shapes: Shape[] };
}

/**
 * Configure ESRI for low-memory environments (8GB RAM Windows machines)
 */
function configureEsriForLowMemory() {
  // Enable memory checking to help ESRI manage resources
  esriConfig.workers.loaderConfig = {
    has: { 'esri-memory-check': true },
  };
}

/**
 * Create the Map and MapView instances and returns them
 */
export async function createMap({
  mapContainer,
  esriMapOptions,
  esriViewOptions,
}: CreateMapParams) {
  // Apply low-memory configuration before creating map
  configureEsriForLowMemory();

  const { hasAlternativeBasemap, alternativeBasemapItemIds, ...mapOptions } =
    esriMapOptions;
  const basemap = await esriBasemapFromUiStringWithFallback(
    mapOptions.basemap ? mapOptions.basemap : 'arcgis-navigation',
    { hasAlternativeBasemap, alternativeBasemapItemIds },
  );

  const map = new Map({
    ...mapOptions,
    basemap,
  });

  const view = new MapView({
    ...esriViewOptions,
    center: esriViewOptions.center,
    container: mapContainer, // mapRef.current,
    map,
    zoom: 4,
    extent: normalizeExtent(esriViewOptions.shapes),
    constraints: {
      rotationEnabled: true,
      // Limit zoom range to reduce feature queries at extreme zoom levels
      minZoom: 3,
      maxZoom: 22,
    },
  });

  // Arrow cursor instead of the Esri default hand/grab (MapViewProperties typings omit `cursor`)
  (view as __esri.MapView & { cursor: string }).cursor = 'default';

  const attribution = new Attribution({
    view,
  });

  view.ui.add(attribution, 'manual');

  return { map, view, attribution };
}
